<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue';
import type { TourStep } from './types';

/**
 * Reusable gamification onboarding tour shell (M4C-13).
 *
 * Hosts all five onboarding steps: a centered, blurred overlay on desktop and a
 * full-width modal on mobile. The shell owns the chrome (lime-bordered card,
 * progress header, footer nav) and the step state (current index, next / back /
 * skip / finish); only the body changes per step. The step list is data-driven
 * via `steps` - each step renders its `component`, or falls back to the `#body`
 * slot. Visibility is controlled by the parent (the trigger, M4C-27) through
 * `v-model:visible`.
 */
const props = withDefaults(
  defineProps<{
    /** Controls overlay visibility. Use with `v-model:visible`. */
    visible?: boolean;
    /** Data-driven step list - drives progress, the label and body content. */
    steps?: TourStep[];
    /** 1-based step to start on each time the tour opens. */
    initialStep?: number;
  }>(),
  {
    visible: false,
    steps: () => [],
    initialStep: 1,
  },
);

const emit = defineEmits<{
  'update:visible': [value: boolean];
  /** Emitted when the user skips the tour (first-step left action). */
  skip: [];
  /** Emitted when the user completes the tour (Continue on the final step). */
  finish: [];
  /** Emitted whenever the active step changes, with the new 1-based step. */
  'step-change': [step: number];
}>();

const { t } = useI18n();

const currentStep = ref(props.initialStep);
const overlayRef = ref<HTMLElement | null>(null);

const totalSteps = computed(() => Math.max(props.steps.length, 1));
const currentIndex = computed(() => currentStep.value - 1);
const isFirstStep = computed(() => currentStep.value <= 1);
const isLastStep = computed(() => currentStep.value >= totalSteps.value);
const activeStep = computed<TourStep | undefined>(() => props.steps[currentIndex.value]);
const activeComponent = computed(() => activeStep.value?.component);

/**
 * Reset to the initial step each time the tour opens and lock the page scroll
 * behind the overlay while it is visible.
 */
watch(
  () => props.visible,
  (isVisible) => {
    if (isVisible) {
      currentStep.value = props.initialStep;
    }
    if (import.meta.client) {
      document.body.style.overflow = isVisible ? 'hidden' : '';
      // Move focus into the dialog on open so Escape works and screen readers
      // enter the modal context.
      if (isVisible) {
        nextTick(() => overlayRef.value?.focus());
      }
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (import.meta.client) {
    document.body.style.overflow = '';
  }
});

function dismiss(): void {
  emit('update:visible', false);
}

function next(): void {
  if (isLastStep.value) {
    emit('finish');
    dismiss();
    return;
  }
  currentStep.value += 1;
  emit('step-change', currentStep.value);
}

function back(): void {
  if (isFirstStep.value) {
    return;
  }
  currentStep.value -= 1;
  emit('step-change', currentStep.value);
}

function skip(): void {
  emit('skip');
  dismiss();
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="visible"
        ref="overlayRef"
        tabindex="-1"
        class="fixed inset-0 z-[1100] flex justify-center overflow-y-auto bg-white focus:outline-none md:items-center md:bg-white/30 md:p-4 md:backdrop-blur-[7.5px]"
        role="dialog"
        aria-modal="true"
        :aria-label="t('onboarding.tour.aria_label')"
        @keydown.esc="skip"
      >
        <!-- Outer frame: 4px white padding + soft shadow around the lime card -->
        <div
          class="flex w-full flex-col self-stretch rounded-[6px] bg-white p-1 shadow-[0_0_7.5px_rgba(77,112,118,0.2)] md:max-w-[976px] md:self-auto"
        >
          <!-- Inner card: lime border, generous padding, header / body / footer -->
          <div
            class="flex flex-1 flex-col rounded-[4px] border border-onboarding-border bg-white p-6 font-app text-fontColor md:min-h-[709px] md:p-16"
          >
            <!-- Header: progress indicator + "X of N" label -->
            <header class="flex w-full shrink-0 items-center justify-between">
              <OnboardingTourProgress :current-step="currentStep" :total-steps="totalSteps" />
              <span class="text-body-strong text-fontColor-w60">
                {{ t('onboarding.tour.step_label', { current: currentStep, total: totalSteps }) }}
              </span>
            </header>

            <!-- Body: per-step content (data-driven component or #body slot) -->
            <div class="flex flex-1 flex-col pt-12 md:pt-16">
              <component :is="activeComponent" v-if="activeComponent" v-bind="activeStep?.props" />
              <slot
                v-else
                name="body"
                :step="activeStep"
                :index="currentIndex"
                :current-step="currentStep"
                :total-steps="totalSteps"
              />
            </div>

            <!-- Footer: Skip tour / Back (left) + Continue (right) -->
            <footer class="flex w-full shrink-0 items-center justify-between pt-8">
              <GamiLink v-if="isFirstStep" @click="skip">
                {{ t('onboarding.tour.skip') }}
              </GamiLink>
              <GamiLink v-else @click="back">
                <svg
                  class="h-4 w-4"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 3.5 5.5 8l4.5 4.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{ t('onboarding.tour.back') }}
              </GamiLink>

              <GamiButton @click="next">
                {{ t('onboarding.tour.continue') }}
              </GamiButton>
            </footer>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
